import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DocumentEditorContainerComponent,
  Search,
  Toolbar,
} from "@syncfusion/ej2-react-documenteditor";
import { ActionIcon, Button, Checkbox, Modal, TextInput } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconTrash, IconChevronUp, IconChevronDown } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import constants from "../utilities/constants";
import { selectUserData } from "../redux/selectors";

DocumentEditorContainerComponent.Inject(Toolbar, Search);

const PLACEHOLDER_EDITOR_ROLE_IDS = [1, 4];
const PLACEHOLDER_TOOLBAR_ID = "create_placeholder";
const EMPTY_VALUES = {};
const EMPTY_PLACEHOLDERS = [];

const PLACEHOLDER_TOOLBAR_ITEM = {
  prefixIcon: "e-plus",
  tooltipText: "Select text, then create a placeholder",
  text: "Create Placeholder",
  id: PLACEHOLDER_TOOLBAR_ID,
};

const TOOLBAR_ITEMS = [
  "Open",
  "Separator",
  "Undo",
  "Redo",
  "Separator",
  "Image",
  "Table",
  "Hyperlink",
  "Separator",
  "Header",
  "Footer",
  "PageSetup",
  "PageNumber",
  "Separator",
  "Break",
  "Find",
];

const replaceAllOccurrences = (editor, search, findText, newValue) => {
  search.findAll(findText);
  const results = search.searchResults;
  if (!results?.length) return false;

  const replacement = String(newValue ?? "");
  for (let i = results.length - 1; i >= 0; i -= 1) {
    results.index = i;
    editor.selection.characterFormat.highlightColor = "NoColor";
    editor.editor.insertText(replacement);
  }
  results.clear();
  return true;
};

const Editor = forwardRef(
  (
    {
      initialContent,
      placeholders = EMPTY_PLACEHOLDERS,
      setPlaceholders,
      disabled = false,
      editNotAllowed = false,
      initialValues,
      onValuesChange,
    },
    ref,
  ) => {
    const editorRef = useRef(null);
    const initialValuesRef = useRef(initialValues);
    initialValuesRef.current = initialValues;
    const onValuesChangeRef = useRef(onValuesChange);
    onValuesChangeRef.current = onValuesChange;

    const currentUser = useSelector(selectUserData) || {};
    const loginAsId = Number(currentUser?.loginAs?.id);
    const canManagePlaceholders = PLACEHOLDER_EDITOR_ROLE_IDS.includes(loginAsId);

    const [editorReady, setEditorReady] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [placeholderName, setPlaceholderName] = useState("");
    const [replaceAll, setReplaceAll] = useState(false);
    const [selectionText, setSelectionText] = useState("");
    const [placeholderToDelete, setPlaceholderToDelete] = useState(null);
    const [values, setValues] = useState(() => initialValues || EMPTY_VALUES);
    const [appliedValues, setAppliedValues] = useState({});
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const appliedValuesRef = useRef(appliedValues);
    appliedValuesRef.current = appliedValues;
    const selectionRangeRef = useRef(null);
    const lastSelectionRef = useRef({ text: "", start: null, end: null });

    const serviceUrl = useMemo(
      () => `${constants.wordeditorApi}/api/documenteditor/`,
      [],
    );

    const toolbarItems = useMemo(() => {
      if (editNotAllowed) return [];
      if (disabled || !canManagePlaceholders) return TOOLBAR_ITEMS;
      return [PLACEHOLDER_TOOLBAR_ITEM, "Separator", ...TOOLBAR_ITEMS];
    }, [canManagePlaceholders, disabled, editNotAllowed]);

    const updateValues = (next) => {
      setValues(next);
      onValuesChangeRef.current?.(next);
    };

    const applyValuesToDocument = (vals) => {
      const editor = editorRef.current?.documentEditor;
      const search = editor?.search || editor?.searchModule;
      if (!editor || !search) return;

      const previousReadOnly = editor.isReadOnly;
      editor.isReadOnly = false;
      const nextApplied = { ...appliedValuesRef.current };

      for (const [name, value] of Object.entries(vals || {})) {
        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ""
        ) {
          continue;
        }
        const newValue = String(value);
        const previousValue = nextApplied[name];
        const replaced =
          replaceAllOccurrences(editor, search, `[[${name}]]`, newValue) ||
          (!!previousValue &&
            replaceAllOccurrences(editor, search, previousValue, newValue));
        if (replaced) nextApplied[name] = newValue;
      }

      appliedValuesRef.current = nextApplied;
      setAppliedValues(nextApplied);
      editor.isReadOnly = previousReadOnly;
    };

    useImperativeHandle(
      ref,
      () => ({
        getSfdt: () => editorRef.current?.documentEditor?.serialize(),
        getDocumentEditor: () => editorRef.current?.documentEditor,
        getPlaceholderValues: () => valuesRef.current,
        applyPlaceholderValues: (vals) =>
          applyValuesToDocument(vals || valuesRef.current),
      }),
      [],
    );

    useEffect(() => {
      if (!initialValues || typeof initialValues !== "object") return;
      setValues((prev) => {
        const next = { ...initialValues, ...prev };
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (
          prevKeys.length === nextKeys.length &&
          nextKeys.every((key) => prev[key] === next[key])
        ) {
          return prev;
        }
        return next;
      });
    }, [initialValues]);

    useEffect(() => {
      if (!editorReady) return;
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;

      editor.layoutType = "Continuous";

      if (!initialContent) return;

      editor.open(JSON.stringify(initialContent));
      appliedValuesRef.current = {};
      setAppliedValues({});
      applyValuesToDocument(initialValuesRef.current);
    }, [editorReady, initialContent]);

    const getSelectedText = () => {
      const editor = editorRef.current?.documentEditor;
      const live = String(editor?.selection?.text || "")
        .replace(/\r|\n/g, "")
        .trim();
      return live || lastSelectionRef.current.text || "";
    };

    const rememberSelection = () => {
      const editor = editorRef.current?.documentEditor;
      const text = String(editor?.selection?.text || "")
        .replace(/\r|\n/g, "")
        .trim();
      if (!text || !editor?.selection) return;
      lastSelectionRef.current = {
        text,
        start: editor.selection.startOffset,
        end: editor.selection.endOffset,
      };
    };

    const handleCreated = useCallback(() => {
      setEditorReady(true);
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;
      editor.enableSpellCheck = true;
      editor.selectionChange = () => rememberSelection();
    }, []);

    const highlightPlaceholder = (editor, name) => {
      const search = editor.search || editor.searchModule;
      if (!search) return;
      search.findAll(`[[${name}]]`);
      const matches =
        search.searchResults || search.textSearchResults?.innerList || [];
      const count = matches.length || 0;
      for (let i = 0; i < count; i += 1) {
        if (search.searchResults) search.searchResults.index = i;
        else editor.selection.select(matches[i]);
        editor.selection.characterFormat.highlightColor = "Yellow";
      }
    };

    const createPlaceholder = () => {
      if (!canManagePlaceholders) return;
      const editor = editorRef.current?.documentEditor;
      const selectedText = getSelectedText();
      if (!selectedText) {
        showNotification({
          color: "yellow",
          title: "Select text first",
          message:
            "Highlight text in the document, then click Create Placeholder.",
        });
        return;
      }
      selectionRangeRef.current = {
        start: editor?.selection?.startOffset ?? lastSelectionRef.current.start,
        end: editor?.selection?.endOffset ?? lastSelectionRef.current.end,
      };
      setSelectionText(selectedText);
      setPlaceholderName("");
      setReplaceAll(false);
      setShowModal(true);
    };

    const handleToolbarClick = (args) => {
      const itemId = String(args?.item?.id || "");
      const itemText = String(args?.item?.text || "").replace(/<[^>]+>/g, "");
      const isCreatePlaceholder =
        itemId === PLACEHOLDER_TOOLBAR_ID ||
        itemId.endsWith(`_${PLACEHOLDER_TOOLBAR_ID}`) ||
        itemText.includes("Create Placeholder");
      if (!isCreatePlaceholder || !canManagePlaceholders) return;
      createPlaceholder();
    };

    const confirmCreatePlaceholder = () => {
      if (!canManagePlaceholders) return;
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;

      const name = placeholderName.trim();
      if (!name) return;

      const placeholder = `[[${name}]]`;
      if (replaceAll) {
        const search = editor.search || editor.searchModule;
        if (!search) return;
        const replaced = replaceAllOccurrences(
          editor,
          search,
          selectionText,
          placeholder,
        );
        if (replaced) highlightPlaceholder(editor, name);
      } else {
        const range = selectionRangeRef.current;
        if (range?.start != null && range?.end != null) {
          editor.selection.select(range.start, range.end);
        }
        editor.selection.characterFormat.highlightColor = "Yellow";
        editor.editor.insertText(placeholder);
      }

      setPlaceholders?.((prev) =>
        Array.isArray(prev) && prev.includes(name)
          ? prev
          : [...(prev || []), name],
      );
      setShowModal(false);
    };

    const placeholderList = Array.isArray(placeholders) ? placeholders : [];

    const confirmDeletePlaceholder = () => {
      if (!canManagePlaceholders || placeholderToDelete == null) return;
      const name = placeholderList[placeholderToDelete];
      setPlaceholders?.((prev) =>
        (prev || []).filter((_, i) => i !== placeholderToDelete),
      );
      if (name) {
        const nextValues = { ...valuesRef.current };
        delete nextValues[name];
        updateValues(nextValues);
      }
      setPlaceholderToDelete(null);
    };

    const replacePlaceholder = (name, value) => {
      const nextValues = { ...valuesRef.current, [name]: value };
      updateValues(nextValues);
      applyValuesToDocument({ [name]: value });
    };

    const movePlaceholder = (from, to) => {
      if (to < 0 || to >= placeholderList.length) return;
      const updated = [...placeholderList];
      const [item] = updated.splice(from, 1);
      updated.splice(to, 0, item);
      setPlaceholders?.(updated);
    };

    return (
      <>
        <div className="relative z-0 isolate flex h-[calc(100dvh-160px)] min-h-[480px] w-full flex-col gap-4 overflow-hidden md:flex-row">
          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border bg-white p-2 shadow-sm md:w-[70%]">
            <DocumentEditorContainerComponent
              id="document-editor"
              ref={editorRef}
              height="100%"
              zIndex={10}
              serviceUrl={serviceUrl}
              enableToolbar={!editNotAllowed}
              toolbarItems={toolbarItems}
              enableSpellCheck
              showPropertiesPane={false}
              created={handleCreated}
              toolbarClick={handleToolbarClick}
            />
          </div>

          <div className="flex h-full w-full shrink-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:w-[320px]">
            <div className="border-b border-slate-200 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-slate-800">
                  Placeholders
                </h2>
                {placeholderList.length > 0 && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                    {placeholderList.length}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                {canManagePlaceholders && !disabled
                  ? "Select text in the document, then click Create Placeholder in the toolbar."
                  : disabled
                    ? "Enter a value and apply it to replace the token in the document."
                    : "Placeholder tokens used in this document."}
              </p>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3 pb-16">
              {placeholderList.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                  No placeholders added.
                </div>
              )}

              {placeholderList.map((placeholder, index) => (
                <div
                  key={`${placeholder}-${index}`}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  {canManagePlaceholders && !disabled ? (
                    <div className="flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded-md bg-slate-100 px-2 py-1 text-[13px] font-medium text-slate-700">
                        {`[[${placeholder}]]`}
                      </code>
                      <div className="flex shrink-0 items-center gap-1">
                        <ActionIcon
                          variant="light"
                          color="gray"
                          onClick={() => movePlaceholder(index, index - 1)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <IconChevronUp size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="gray"
                          onClick={() => movePlaceholder(index, index + 1)}
                          disabled={index === placeholderList.length - 1}
                          title="Move down"
                        >
                          <IconChevronDown size={16} />
                        </ActionIcon>
                        <ActionIcon
                          color="red"
                          variant="light"
                          onClick={() => setPlaceholderToDelete(index)}
                          title="Delete placeholder"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </div>
                    </div>
                  ) : disabled ? (
                    <div className="flex flex-col gap-2">
                      <div className="text-sm font-medium text-slate-700">
                        {placeholder}
                      </div>
                      <TextInput
                        placeholder={`Enter ${placeholder}`}
                        value={values[placeholder] || ""}
                        onChange={(e) =>
                          updateValues({
                            ...valuesRef.current,
                            [placeholder]: e.target.value,
                          })
                        }
                      />
                      <Button
                        size="xs"
                        className="self-end"
                        onClick={() =>
                          replacePlaceholder(placeholder, values[placeholder])
                        }
                      >
                        Apply
                      </Button>
                    </div>
                  ) : (
                    <code className="block truncate rounded-md bg-slate-100 px-2 py-1 text-[13px] font-medium text-slate-700">
                      {`[[${placeholder}]]`}
                    </code>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <Modal
          opened={placeholderToDelete != null}
          onClose={() => setPlaceholderToDelete(null)}
          centered
          title="Delete placeholder?"
          overlayBlur={4}
        >
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-500">
              {placeholderToDelete != null
                ? `Remove “${placeholderList[placeholderToDelete]}” from this template? This cannot be undone.`
                : "Remove this placeholder from the template?"}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="default"
                onClick={() => setPlaceholderToDelete(null)}
              >
                Cancel
              </Button>
              <Button color="red" onClick={confirmDeletePlaceholder}>
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          opened={showModal}
          onClose={() => setShowModal(false)}
          centered
          title="Create Placeholder"
        >
          <div className="flex flex-col gap-4">
            <div className="rounded-md bg-gray-100 p-3 text-sm">
              {selectionText}
            </div>
            <TextInput
              label="Placeholder Name"
              placeholder="Enter placeholder name"
              value={placeholderName}
              onChange={(e) => setPlaceholderName(e.target.value)}
            />
            <Checkbox
              label="Replace all occurrences"
              checked={replaceAll}
              onChange={(e) => setReplaceAll(e.currentTarget.checked)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="default" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button onClick={confirmCreatePlaceholder}>Create</Button>
            </div>
          </div>
        </Modal>
      </>
    );
  },
);

export default Editor;
